import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Plus, Edit, Trash2, BookOpen } from 'lucide-react';

const defaultForm = { title: '', description: '', thumbnail: '', price: 0, category: '', instructor: '', is_published: false, sections: [] };

export default function TenantCourses() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [total, setTotal] = useState(0);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaultForm);

  const fetch = async () => { const r = await api.get('/tenant/courses'); setCourses(r.data.courses); setTotal(r.data.total); };
  useEffect(() => { fetch(); }, []);

  const handleSave = async () => {
    if (editing) await api.put(`/tenant/courses/${editing}`, form);
    else await api.post('/tenant/courses', form);
    setShowDialog(false); setEditing(null); setForm(defaultForm); fetch();
  };
  const handleDelete = async (id) => { if (window.confirm('Hapus kursus?')) { await api.delete(`/tenant/courses/${id}`); fetch(); } };
  const edit = (c) => { setEditing(c.id); setForm(c); setShowDialog(true); };
  const fmt = (v) => new Intl.NumberFormat('id-ID').format(v || 0);

  return (
    <div data-testid="tenant-courses-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>Kursus / LMS ({total})</h1>
        <Button onClick={() => { setEditing(null); setForm(defaultForm); setShowDialog(true); }} className="bg-[#2D6CDF] hover:bg-[#1A5BC4]" data-testid="add-course-btn"><Plus size={16} className="mr-2" /> Tambah Kursus</Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {courses.map(c => (
          <Card key={c.id} className="border-slate-200 shadow-sm overflow-hidden">
            <div className="h-40 bg-gradient-to-br from-[#2D6CDF]/10 to-[#10B981]/10 flex items-center justify-center">
              {c.thumbnail ? <img src={c.thumbnail} alt={c.title} className="w-full h-full object-cover" /> : <span className="text-4xl font-bold text-[#2D6CDF]/20">{c.title[0]}</span>}
            </div>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold text-slate-900">{c.title}</h3>
                  <p className="text-sm text-slate-500 mt-1">{c.instructor || 'No instructor'}</p>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" onClick={() => navigate(`/dashboard/courses/${c.id}`)} title="Edit Konten" data-testid={`edit-content-${c.id}`}><BookOpen size={14} className="text-[#2D6CDF]" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => edit(c)}><Edit size={14} /></Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(c.id)}><Trash2 size={14} className="text-red-500" /></Button>
                </div>
              </div>
              <div className="flex items-center justify-between mt-3">
                <span className="font-bold text-[#2D6CDF]">Rp {fmt(c.price)}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${c.is_published ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{c.is_published ? 'Published' : 'Draft'}</span>
              </div>
              <p className="text-xs text-slate-500 mt-2">{c.enrollment_count || 0} enrolled</p>
            </CardContent>
          </Card>
        ))}
        {!courses.length && <div className="col-span-full py-12 text-center text-slate-400">Belum ada kursus</div>}
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing ? 'Edit Kursus' : 'Tambah Kursus'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Judul Kursus</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="course-title-input" /></div>
            <div><Label>Deskripsi</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Harga (Rp)</Label><Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: parseInt(e.target.value) || 0 })} /></div>
              <div><Label>Kategori</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></div>
            </div>
            <div><Label>Instructor</Label><Input value={form.instructor} onChange={(e) => setForm({ ...form, instructor: e.target.value })} /></div>
            <div><Label>Thumbnail URL</Label><Input value={form.thumbnail} onChange={(e) => setForm({ ...form, thumbnail: e.target.value })} /></div>
            <div className="flex items-center gap-2"><Switch checked={form.is_published} onCheckedChange={(v) => setForm({ ...form, is_published: v })} /><Label>Published</Label></div>
            <Button onClick={handleSave} className="w-full bg-[#2D6CDF] hover:bg-[#1A5BC4]" data-testid="save-course-btn">Simpan</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
